import type { MapData, Rect, Vec2, ZoneDef } from "./types.js";
import { isWalkableTile } from "./types.js";

/** Player feet collision box size. */
export const PLAYER_BOX_W = 10;
export const PLAYER_BOX_H = 6;

/** The collision rectangle for a player whose feet are at `(x, y)`. */
export function footBox(x: number, y: number): Rect {
  return { x: x - PLAYER_BOX_W / 2, y: y - PLAYER_BOX_H, w: PLAYER_BOX_W, h: PLAYER_BOX_H };
}

export function isRectWalkable(map: MapData, r: Rect): boolean {
  const ts = map.tileSize;
  const minTx = Math.floor(r.x / ts);
  const maxTx = Math.floor((r.x + r.w - 0.001) / ts);
  const minTy = Math.floor(r.y / ts);
  const maxTy = Math.floor((r.y + r.h - 0.001) / ts);
  for (let ty = minTy; ty <= maxTy; ty++) {
    for (let tx = minTx; tx <= maxTx; tx++) {
      if (!isWalkableTile(map, tx, ty)) return false;
      const shapes = map.shapeColliders.get(ty * map.width + tx);
      if (shapes?.some((s) => rectsOverlap(r, s))) return false;
    }
  }
  return true;
}

/** Strict overlap: rectangles that only touch along an edge do not collide. */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** Whether a player standing with feet at `(x, y)` fits on walkable tiles. */
export function isPositionWalkable(map: MapData, x: number, y: number): boolean {
  return isRectWalkable(map, footBox(x, y));
}

/**
 * Move from `from` towards `to`, sub-stepping in ≤ 4 px increments and sliding
 * along walls (X and Y are tested independently each step). Returns the last
 * walkable position.
 */
export function clampMove(map: MapData, from: Vec2, to: Vec2): Vec2 {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.hypot(dx, dy);
  if (dist === 0) return { x: from.x, y: from.y };

  const steps = Math.max(1, Math.ceil(dist / 4));
  const sx = dx / steps;
  const sy = dy / steps;
  let x = from.x;
  let y = from.y;

  // Advance from the last accepted position so a blocked axis can never skip
  // across a thin obstacle on a later substep.
  for (let i = 1; i <= steps; i++) {
    const nx = x + sx;
    const ny = y + sy;
    if (isPositionWalkable(map, nx, y)) x = nx;
    if (isPositionWalkable(map, x, ny)) y = ny;
  }
  return { x, y };
}

export function pointInRect(p: Vec2, r: Rect): boolean {
  return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
}

/** Ray-casting point-in-polygon test. */
export function pointInPolygon(p: Vec2, points: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i]!;
    const b = points[j]!;
    const intersect =
      a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function inZone(p: Vec2, zone: ZoneDef): boolean {
  if (zone.shape === "rect" && zone.rect) return pointInRect(p, zone.rect);
  if (zone.shape === "polygon" && zone.points) return pointInPolygon(p, zone.points);
  return false;
}

export function inAnyZone(p: Vec2, zones: ZoneDef[]): boolean {
  return zones.some((z) => inZone(p, z));
}

export function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
